export const CMS_MEDIA_FILE_LIMIT_BYTES = 25 * 1024 * 1024;
import { CMS_BLOCK_METADATA } from "./cms-catalog";
import type { CmsField } from "./cms-schema";

export const CMS_DOCUMENT_LIMITS = Object.freeze({
  blocks: 300,
  depth: 8,
  items: 100,
  tabs: 20,
  text: 16000,
  nodes: 20000,
  codeUnits: 262144,
});
export const CMS_PUBLIC_ENTITY_FIELDS = [
  "name",
  "title",
  "code",
  "description",
  "airportCode",
  "city",
  "state",
  "country",
  "website",
  "phone",
  "manufacturer",
  "model",
  "year",
] as const;
export const CMS_PERSONAL_FIELDS = [
  "user.firstName",
  "user.lastName",
  "user.name",
] as const;
export class CmsDocumentValidationError extends Error {
  constructor(
    readonly code:
      | "CMS_DOCUMENT_LIMIT"
      | "CMS_FIELD_TYPE_INVALID"
      | "CMS_BINDING_FORBIDDEN"
      | "CMS_LINK_INVALID",
  ) {
    super("CMS document contains invalid or unsupported configuration");
  }
}
function fail(code: CmsDocumentValidationError["code"]): never {
  throw new CmsDocumentValidationError(code);
}
const metadata = new Map(
  CMS_BLOCK_METADATA.map((block) => [block.type, block]),
);
const object = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));
/** Structural child slots shared by validation, server targeting and editing. */
export function mapCmsChildBlockLists<
  T extends { type: string; props: Record<string, unknown> },
>(block: T, map: (children: T[]) => T[]): T {
  if (["content_group", "public_collection"].includes(block.type))
    return {
      ...block,
      props: {
        ...block.props,
        blocks: map(
          Array.isArray(block.props.blocks)
            ? (block.props.blocks.filter(
                (item) => object(item) && typeof item.type === "string",
              ) as T[])
            : [],
        ),
      },
    };
  if (block.type !== "tabbed_container" || !Array.isArray(block.props?.tabs))
    return block;
  return {
    ...block,
    props: {
      ...block.props,
      tabs: block.props.tabs.filter(object).map((tab) => ({
        ...tab,
        blocks: map(
          Array.isArray(tab.blocks)
            ? (tab.blocks.filter(
                (item) => object(item) && typeof item.type === "string",
              ) as T[])
            : [],
        ),
      })),
    },
  };
}
/** Replace one known owned slot, retaining all other props and metadata. */
export function replaceCmsChildBlocks<
  T extends { type: string; props: Record<string, unknown> },
>(block: T, slot: number, children: unknown[]): T {
  if (["content_group", "public_collection"].includes(block.type) && slot === 0)
    return { ...block, props: { ...block.props, blocks: children } };
  if (block.type === "tabbed_container" && Array.isArray(block.props.tabs))
    return {
      ...block,
      props: {
        ...block.props,
        tabs: block.props.tabs.map((tab, index) =>
          index === slot && object(tab) ? { ...tab, blocks: children } : tab,
        ),
      },
    };
  return block;
}

export function cmsPublicEntityTokens(
  value: Record<string, unknown> | null | undefined,
  prefix: "entity" | "item" = "entity",
): Record<string, string> {
  const result: Record<string, string> = {};
  if (!value) return result;
  for (const key of CMS_PUBLIC_ENTITY_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
    const field = value[key];
    if (typeof field === "string")
      result[`${prefix}.${key}`] = field.slice(0, 16000);
    else if (typeof field === "number" && Number.isFinite(field))
      result[`${prefix}.${key}`] = String(field);
  }
  return result;
}
export function cmsBindingAllowed(key: string): boolean {
  if (
    (CMS_PERSONAL_FIELDS as readonly string[]).includes(key) ||
    key === "app_name" ||
    key === "year"
  )
    return true;
  const [scope, field, extra] = key.split(".");
  return (
    !extra &&
    ["entity", "item"].includes(scope) &&
    (CMS_PUBLIC_ENTITY_FIELDS as readonly string[]).includes(field)
  );
}
function checkTokens(value: string) {
  for (const match of value.matchAll(/\{\{\s*([^{}|]+)(?:\|[^{}]*)?\}\}/g)) {
    const key = match[1].trim();
    // Historical unknown literal tokens remain literal. These namespaces are
    // data bindings, and must never become arbitrary object traversal.
    if (/^(entity|item|user)(\.|$)/.test(key) && !cmsBindingAllowed(key))
      fail("CMS_BINDING_FORBIDDEN");
  }
}
function safeLink(value: string, image: boolean) {
  if (!value) return;
  if (/\{\{\s*user[.]/.test(value)) fail("CMS_BINDING_FORBIDDEN");
  if (/^https?:\/\/[^/?#]*@/i.test(value)) fail("CMS_LINK_INVALID");
  if (
    /^[\u0000-\u0020]|[\u0000-\u001f\u007f]/.test(value) ||
    value.startsWith("//") ||
    value.includes("\\")
  )
    fail("CMS_LINK_INVALID");
  if (
    /^[a-z][a-z0-9+.-]*:/i.test(value) &&
    !(image ? /^https?:\/\//i : /^(https?:\/\/|mailto:|tel:)/i).test(value)
  )
    fail("CMS_LINK_INVALID");
}

/** Validate authoring data before any DB write. Never mutates or strips content. */
export function validateCmsDocument(input: unknown): void {
  let nodes = 0,
    blocks = 0;
  const seen = new Set<object>();
  function json(value: unknown, depth: number) {
    if (++nodes > CMS_DOCUMENT_LIMITS.nodes || depth > 64)
      fail("CMS_DOCUMENT_LIMIT");
    if (typeof value === "string") {
      if (value.length > CMS_DOCUMENT_LIMITS.text) fail("CMS_DOCUMENT_LIMIT");
      checkTokens(value);
      return;
    }
    if (
      value === undefined ||
      value === null ||
      typeof value === "boolean" ||
      (typeof value === "number" && Number.isFinite(value))
    )
      return;
    if (!value || typeof value !== "object") fail("CMS_FIELD_TYPE_INVALID");
    if (seen.has(value)) fail("CMS_FIELD_TYPE_INVALID");
    seen.add(value);
    if (Array.isArray(value)) {
      if (value.length > CMS_DOCUMENT_LIMITS.blocks) fail("CMS_DOCUMENT_LIMIT");
      value.forEach((child) => json(child, depth + 1));
    } else {
      const entries = Object.entries(value);
      if (entries.length > 100) fail("CMS_DOCUMENT_LIMIT");
      for (const [key, child] of entries) {
        if (["__proto__", "prototype", "constructor"].includes(key))
          fail("CMS_FIELD_TYPE_INVALID");
        json(child, depth + 1);
      }
    }
    seen.delete(value);
  }
  function field(value: unknown, definition: CmsField, depth: number) {
    if (value === undefined || value === null) return;
    switch (definition.type) {
      case "boolean":
        if (typeof value !== "boolean") fail("CMS_FIELD_TYPE_INVALID");
        return;
      case "number":
        if (
          typeof value !== "number" ||
          !Number.isFinite(value) ||
          Math.abs(value) > 1000000 ||
          (definition.min !== undefined && value < definition.min) ||
          (definition.max !== undefined && value > definition.max)
        )
          fail("CMS_FIELD_TYPE_INVALID");
        return;
      case "richtext":
        if (
          !Array.isArray(value) ||
          value.length > CMS_DOCUMENT_LIMITS.items ||
          value.some((item) => typeof item !== "string")
        )
          fail("CMS_FIELD_TYPE_INVALID");
        return;
      case "list":
        if (
          !Array.isArray(value) ||
          value.length >
            Math.min(
              definition.max ?? CMS_DOCUMENT_LIMITS.items,
              CMS_DOCUMENT_LIMITS.items,
            )
        )
          fail("CMS_DOCUMENT_LIMIT");
        for (const item of value) {
          if (!object(item)) fail("CMS_FIELD_TYPE_INVALID");
          for (const child of definition.fields)
            field(item[child.key], child, depth);
        }
        return;
      case "blocks":
        if (!Array.isArray(value)) fail("CMS_FIELD_TYPE_INVALID");
        if (
          definition.allowedTypes &&
          value.some(
            (block) =>
              !object(block) ||
              !definition.allowedTypes!.includes(String(block.type)),
          )
        )
          fail("CMS_FIELD_TYPE_INVALID");
        document(value, depth + 1);
        return;
      case "tabs":
        if (
          !Array.isArray(value) ||
          value.length >
            Math.min(
              definition.max ?? CMS_DOCUMENT_LIMITS.tabs,
              CMS_DOCUMENT_LIMITS.tabs,
            )
        )
          fail("CMS_DOCUMENT_LIMIT");
        for (const tab of value) {
          if (!object(tab) || typeof tab.label !== "string")
            fail("CMS_FIELD_TYPE_INVALID");
          document(tab.blocks, depth + 1);
        }
        return;
      default:
        if (typeof value !== "string") fail("CMS_FIELD_TYPE_INVALID");
        if (
          definition.type === "select" &&
          value &&
          !definition.options.some((option) => option.value === value)
        )
          fail("CMS_FIELD_TYPE_INVALID");
        if (definition.type === "link" || definition.type === "image")
          safeLink(value, definition.type === "image");
    }
  }
  function document(value: unknown, depth: number) {
    if (!Array.isArray(value)) fail("CMS_FIELD_TYPE_INVALID");
    if (depth > CMS_DOCUMENT_LIMITS.depth) fail("CMS_DOCUMENT_LIMIT");
    for (const block of value) {
      if (++blocks > CMS_DOCUMENT_LIMITS.blocks) fail("CMS_DOCUMENT_LIMIT");
      if (
        !object(block) ||
        typeof block.type !== "string" ||
        block.type.length > 80 ||
        (block.props != null && !object(block.props))
      )
        fail("CMS_FIELD_TYPE_INVALID");
      if (
        block.id !== undefined &&
        (typeof block.id !== "string" || block.id.length > 128)
      )
        fail("CMS_FIELD_TYPE_INVALID");
      if (block.hidden !== undefined && typeof block.hidden !== "boolean")
        fail("CMS_FIELD_TYPE_INVALID");
      // Unknown historical blocks/extensions stay opaque and bounded; the
      // registered renderer skips them. Known fields use the actual UI schema.
      const props = (block.props ?? {}) as Record<string, unknown>;
      for (const definition of metadata.get(block.type)?.fields ?? [])
        field(props[definition.key], definition, depth);
    }
  }
  json(input, 0);
  let serialized: string;
  try {
    serialized = JSON.stringify(input);
  } catch {
    fail("CMS_FIELD_TYPE_INVALID");
  }
  if (serialized!.length > CMS_DOCUMENT_LIMITS.codeUnits)
    fail("CMS_DOCUMENT_LIMIT");
  document(input, 0);
}
